// src/features/rooms/SelectedRoomsActionsMenu.tsx
import React, { useMemo, useState } from "react";
import { Button, Group, Menu, Text } from "@mantine/core";
import {
  FaChevronDown,
  FaTrash,
  FaLayerGroup,
  FaCheckCircle,
  FaProjectDiagram,
} from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectDevicesInSelectedRooms } from "../deviceSelectors"; // ⚠️ juster path
import { DeleteDevicesInSelectedRoomsModal } from "./DeleteDevicesInSelectedRoomsModal";
import { PlaceDevicesFromStandardRoomsModal } from "./PlaceDevicesFromStandardRoomsModal";
import { AutoPipeRoutingModal } from "../../routing/autoPipeRouting/AutoPipeRoutingModal";
import { setAutoPipeRoutingModalOpenInRedux } from "../../pipeRoutingSlice";
import {
  setActiveLeftSideBarTabInRedux,
  setRunSpaceValidationsInSelectedRoomsInRedux,
} from "../../designSlice"; // ⚠️ juster path
import { DesignLeftSideBarTabs } from "@multiconsult-group/multiplacer-core";

export const SelectedRoomsActionsMenu: React.FC = () => {
  const dispatch = useAppDispatch();

  const selectedRooms = useAppSelector((s) => s.design.selectedRooms);
  const devicesInSelectedRooms = useAppSelector(selectDevicesInSelectedRooms);

  const selectedCount = selectedRooms?.length ?? 0;
  const hasSelectedRooms = selectedCount > 0;

  const hasDevicesInSelectedRooms = (devicesInSelectedRooms?.length ?? 0) > 0;

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [placeFromStdModalOpen, setPlaceFromStdModalOpen] = useState(false);

  const label = useMemo(() => {
    if (selectedCount === 0) return "Ingen rom valgt";
    if (selectedCount === 1) return "1 rom valgt";
    return `${selectedCount} rom valgt`;
  }, [selectedCount]);

  const handleRunRules = () => {
    dispatch(setRunSpaceValidationsInSelectedRoomsInRedux(true));
    dispatch(setActiveLeftSideBarTabInRedux(DesignLeftSideBarTabs.CEILING_SETTINGS));
  };

  return (
    <>
      <Group gap="sm" justify="space-between" wrap="nowrap">
        <Text size="sm" fw={600}>
          {label}
        </Text>

        <Menu shadow="md" width={320} position="bottom-end">
          <Menu.Target>
            <Button
              size="xs"
              variant="filled"
              rightSection={<FaChevronDown size={12} />}
              disabled={!hasSelectedRooms}
            >
              Handlinger
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label>Valgte rom</Menu.Label>

            {/* Alltid tilgjengelig – modalen forklarer status */}
            <Menu.Item
              leftSection={<FaLayerGroup size={14} />}
              onClick={() => setPlaceFromStdModalOpen(true)}
            >
              Plasser ut komponenter fra standardrom
            </Menu.Item>

            <Menu.Item
              leftSection={<FaProjectDiagram size={14} />}
              onClick={() => dispatch(setAutoPipeRoutingModalOpenInRedux(true))}
            >
              Automatisk rordragning
            </Menu.Item>

            <Menu.Divider />

            {/* ✅ Kjør regler: kun hvis valgte rom faktisk har enheter */}
            <Menu.Item
              leftSection={<FaCheckCircle size={14} />}
              disabled={!hasDevicesInSelectedRooms}
              onClick={handleRunRules}
            >
              Kjør regler på valgte rom
            </Menu.Item>

            {!hasDevicesInSelectedRooms && (
              <Text size="xs" c="dimmed" px="sm" pb="xs">
                Velg rom som inneholder enheter for å kjøre regler.
              </Text>
            )}

            <Menu.Divider />

            {/* Slett: kun hvis det faktisk finnes enheter i valgte rom */}
            <Menu.Item
              leftSection={<FaTrash size={14} />}
              color="red"
              disabled={!hasDevicesInSelectedRooms}
              onClick={() => setDeleteModalOpen(true)}
            >
              Slett alle enheter i valgte rom
            </Menu.Item>

            {!hasDevicesInSelectedRooms && (
              <Menu.Item disabled>Ingen enheter i valgte rom</Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>

      <DeleteDevicesInSelectedRoomsModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
      />

      <PlaceDevicesFromStandardRoomsModal
        opened={placeFromStdModalOpen}
        onClose={() => setPlaceFromStdModalOpen(false)}
      />

      <AutoPipeRoutingModal />
    </>
  );
};
